import { getNavigation, getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import {
  MatchListItemFragment,
  SeasonFragment,
  TeamContactFragment,
  TeamDetailItemFragment,
} from '@amf/shared/types/graphql';
import Container from '@amf/shared/components/layout/Container';
import { GetServerSideProps } from 'next';
import { sdk } from '@amf/shared/lib/graphQlClient';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import TeamRoster from '@amf/shared/components/team/TeamRoster';
import { useTranslate } from '@tolgee/react';
import TeamContact from '@amf/shared/components/team/TeamContact';
import { useMemo } from 'react';
import RouterMenu, { RouterMenuItem } from '@amf/shared/components/menu/RouterMenu';
import { getMatches, LIMIT } from '@amf/shared/data/Match';
import TeamMatches from '@amf/shared/components/team/TeamMatches';
import Content from '@amf/shared/components/content/Content';
import PlayerNews from '@amf/shared/components/player/PlayerNews';
import TeamStats from '@amf/shared/components/team/TeamStats';
import TeamPlayerStats from '@amf/shared/components/team/TeamPlayerStats';

import Meta from 'components/Meta';
import Layout from 'components/layout/Layout';
import { AppLinks } from 'utils/links';
import TeamHeader from 'components/team/TeamHeader';

enum TeamDetailPageSection {
  about = '',
  matches = 'matches',
  stats = 'stats',
}

interface TeamDetailPageProps extends NavigationProps {
  team: TeamDetailItemFragment;
  section: TeamDetailPageSection;
  contact: TeamContactFragment | null;
  seasons: SeasonFragment[];
  matches: MatchListItemFragment[];
}

export default function TeamDetailPage({
  navigation,
  team,
  contact,
  section,
  seasons,
  matches,
  partners,
}: TeamDetailPageProps) {
  const { t } = useTranslate('general');

  const menu: RouterMenuItem[] = useMemo(() => {
    return [
      {
        title: t('team.menu.about'),
        url: `${AppLinks.team}/${team.id}`,
        selected: section === TeamDetailPageSection.about,
      },
      {
        title: t('team.menu.matches'),
        url: `${AppLinks.team}/${team.id}/${TeamDetailPageSection.matches}`,
        selected: section === TeamDetailPageSection.matches,
      },
      {
        title: t('team.menu.stats'),
        url: `${AppLinks.team}/${team.id}/${TeamDetailPageSection.stats}`,
        selected: section === TeamDetailPageSection.stats,
      },
    ];
  }, [section, t, team.id]);

  return (
    <Layout navigation={navigation} partners={partners}>
      <Meta title={team.name} />
      <TeamHeader team={team} prefixes={{ club: AppLinks.club }} />
      <RouterMenu items={menu} className='!top-20' />
      <Container>
        {seasons.length > 0 && section !== TeamDetailPageSection.matches && (
          <TeamStats
            team={team}
            seasons={seasons}
            title={t('team.stats.title')}
            prefixes={{ table: AppLinks.table }}
          />
        )}
        {seasons.length > 0 && section === TeamDetailPageSection.stats && (
          <TeamPlayerStats
            team={team}
            seasons={seasons}
            title={t('team.playerStats.title')}
            prefixes={{ player: AppLinks.player }}
          />
        )}
        {seasons.length > 0 && section === TeamDetailPageSection.about && (
          <TeamRoster
            seasons={seasons}
            team={team}
            title={t('team.roster.title')}
            prefix={AppLinks.player}
          />
        )}
        {team.content && section === TeamDetailPageSection.about && (
          <Content content={team.content} />
        )}
        {contact && (
          <TeamContact
            team={team}
            contact={contact}
            title={t('team.contact.title')}
            prefixes={{ player: AppLinks.player }}
          />
        )}
        {section === TeamDetailPageSection.matches && (
          <TeamMatches
            title={t('team.matches.title')}
            matches={matches}
            seasons={seasons}
            team={team}
            matchPrefix={AppLinks.match}
          />
        )}
        {section === TeamDetailPageSection.about && team.news.length > 0 && (
          <PlayerNews
            title={t('team.news')}
            news={team.news}
            prefixes={{ news: AppLinks.newsList }}
          />
        )}
      </Container>
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps<TeamDetailPageProps, { params: string[] }> =
  async ({ locale, params: { params }, res }) => {
    const [id, sectionRaw] = params;
    const { team } = await sdk.TeamDetail({ union: process.env.NEXT_PUBLIC_UNION, id });

    if (!team) {
      return {
        notFound: true,
      };
    }

    res.setHeader('Cache-Control', 'max-age=0, s-maxage=3600');
    const section = sectionRaw
      ? (sectionRaw as TeamDetailPageSection)
      : TeamDetailPageSection.about;

    const { seasons } = await sdk.SeasonList({ union: process.env.NEXT_PUBLIC_UNION });
    const filteredSeasons = seasons.filter(s => s.leagues.length > 0);

    if (section === TeamDetailPageSection.about) {
      const { contact } = await sdk.TeamContact({ union: process.env.NEXT_PUBLIC_UNION, id });
      if (!contact) {
        return {
          notFound: true,
        };
      }

      return {
        props: {
          team,
          section,
          contact,
          seasons: filteredSeasons,
          matches: [],
          navigation: await getNavigation(getLanguageLocale(locale!)),
          partners: await getPartners(),
        },
      };
    }

    let matches: MatchListItemFragment[] = [];
    if (filteredSeasons.length > 0) {
      matches = await getMatches(filteredSeasons[0].leagues[0].id, 0, LIMIT, team.id);
    }

    return {
      props: {
        team,
        section,
        seasons: filteredSeasons,
        matches,
        contact: null,
        navigation: await getNavigation(getLanguageLocale(locale!)),
        partners: await getPartners(),
      },
    };
  };
